/** @format */

import React, { Suspense } from "react";
import { Box } from "@mui/material";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { AuthProvider } from "contexts/AuthProvider";
import { DataProvider } from "contexts/DataContext";
import { Layout } from "pages/Layout";
import { ProtectedSignIn, AdminRoutes } from "components/ProtectedRoute";
import {
  POWER_QUALITY_MENU,
  MENU_NAMES,
  SOLUTION_MENU,
  CONTACT_MENU,
  RESOURCE_MENU,
  SPD_MENU,
  CUSTOMER_SUPPORT_MENU,
} from "const";
import ScrollToTop from "utils/scroll-to-top";
import PageTransition from "./page-transition";

const Home = React.lazy(() => import("pages/Main/Home"));
const VerifyEmailPage = React.lazy(() => import("features/auth/verify-email"));
const ResetPassword = React.lazy(() => import("features/auth/reset-password"));
const NotFound = React.lazy(() => import("pages/not-found"));
const Profile = React.lazy(() => import("pages/profile/profile"));

const Surge = React.lazy(() => import("pages/Solutions/Surge"));
const HarmonicSolution = React.lazy(
  () => import("pages/Solutions/HarmonicSolution"),
);
const ReactivePowerSolution = React.lazy(
  () => import("pages/Solutions/ReactivePowerSolution"),
);
const Catalog = React.lazy(() => import("pages/Resource/catalog"));

const News = React.lazy(() => import("pages/Resource/news"));
const Data = React.lazy(() => import("pages/Resource/certificate"));
const DataDetail = React.lazy(
  () => import("pages/Resource/certificate-detail"),
);
const CreateDoc = React.lazy(() => import("pages/Resource/create-doc"));
const NewsDetail = React.lazy(() => import("pages/Resource/news-detail"));

const Company = React.lazy(() => import("pages/Contact/company"));
const Videos = React.lazy(() => import("pages/Contact/videos"));
const Solutions = React.lazy(() => import("pages/Solutions/slpms"));
const CEOGreeting = React.lazy(() => import("pages/Contact/ceo-greeting"));
const History = React.lazy(() => import("pages/Contact/history"));
const Location = React.lazy(() => import("pages/Contact/location"));
const Customers = React.lazy(() => import("pages/Contact/customers"));
const Organization = React.lazy(() => import("pages/Contact/organization"));
const SPDCoordination = React.lazy(
  () => import("pages/Solutions/spd-coordination"),
);
const InquiryDetail = React.lazy(() => import("pages/inquiry/inquiry-detail"));
const ZeroSequenceHarmonicFilter = React.lazy(
  () => import("pages/Products/power-quality/harmonic-reduction/pzf"),
);
const ActiveHarmonicFilter = React.lazy(
  () => import("pages/Products/power-quality/harmonic-reduction/paf"),
);

const ProductShell = React.lazy(() => import("pages/Layout/product-shell"));
const SpdCategory = React.lazy(() => import("pages/Products/spd"));

const HarmonicReductionCategory = React.lazy(
  () => import("pages/Products/power-quality/harmonic-reduction"),
);

const ReactivePowerImprovementCategory = React.lazy(
  () => import("pages/Products/power-quality/reactive-power-improvement"),
);

const PSVGSeries = React.lazy(
  () => import("pages/Products/power-quality/reactive-power-improvement/psvg"),
);

const PSTASeries = React.lazy(
  () => import("pages/Products/power-quality/reactive-power-improvement/psta"),
);

const PSVCSeries = React.lazy(
  () => import("pages/Products/power-quality/reactive-power-improvement/psvc"),
);

const NoiseFilter = React.lazy(
  () => import("pages/Products/power-quality/noise-reduction/plf"),
);
const NoiseCutTransformer = React.lazy(
  () => import("pages/Products/power-quality/noise-reduction/ptr"),
);

//-------------- SPD ----------------------
const SPDExtra = React.lazy(() => import("pages/Products/spd/spd-extra"));
const SpdExternalDisconnector = React.lazy(
  () => import("pages/Products/spd/spd-external-disconnector"),
);
const SpdForCommunication = React.lazy(
  () => import("pages/Products/spd/spd-for-communication"),
);
const PsSeries = React.lazy(
  () => import("pages/Products/spd/spd-for-power/ps-series"),
);
const PSDCSeries = React.lazy(
  () => import("pages/Products/spd/spd-for-power/psdc-series"),
);
const RSeries = React.lazy(
  () => import("pages/Products/spd/spd-for-power/r-series"),
);
const SbSeries = React.lazy(
  () => import("pages/Products/spd/spd-for-power/sb-series"),
);
const SgSeries = React.lazy(
  () => import("pages/Products/spd/spd-for-power/sg-series"),
);

const InquiryForm = React.lazy(() => import("pages/Services/consultation"));
const Inquiry = React.lazy(() => import("pages/inquiry"));
const QuoteForm = React.lazy(() => import("pages/Services/quote-form"));
const MyQuote = React.lazy(() => import("pages/quote"));

// [보류] 견적 문의 카트 비활성화 (발주 카트만 사용). 재사용 가능성이 있어 삭제하지 않는다.
// const InquiryCart = React.lazy(() => import("pages/cart/inquiry"));
const QuoteCart = React.lazy(() => import("pages/cart/quote"));
const QuoteDetail = React.lazy(() => import("pages/quote/quote-detail"));

const AdminPage = React.lazy(() => import("pages/admin"));

// v7_startTransition: 페이지 코드를 내려받는 동안 이전 화면을 유지한다.
// 켜지 않으면 그 사이 Suspense 대체 화면이 잠깐 보여 깜빡인다.
const AppRouter = () => (
  <Router future={{ v7_startTransition: true }}>
    <AuthProvider>
      <DataProvider>
        <Layout>
          <ScrollToTop />
          {/* 지연 로딩 경계를 본문에만 둔다. 바깥에 두면 페이지를 옮길 때마다
              헤더와 우측 메뉴까지 사라졌다 다시 그려져 화면이 깜빡인다. */}
          <Suspense fallback={<Box sx={{ minHeight: "60vh" }} />}>
            <PageTransition>
              <Routes>
                <Route
                  path="/admin"
                  element={
                    <AdminRoutes>
                      <AdminPage />
                    </AdminRoutes>
                  }
                />
                <Route path="/" element={<Home />} />

                <Route path="my-inquiry" element={<Inquiry />}>
                  <Route path=":id" element={<InquiryDetail />} />
                </Route>
                <Route path="my-quote" element={<MyQuote />}>
                  <Route path=":id" element={<QuoteDetail />} />
                </Route>

                <Route path="cart">
                  {/* [보류] 견적 문의 카트 비활성화 */}
                  {/* <Route path="inquiry" element={<InquiryCart />} /> */}
                  <Route path="quote" element={<QuoteCart />} />
                </Route>
                {/* Products */}
                {/* 우측 제품 선택 메뉴를 하위 전 페이지에 적용한다. */}
                <Route
                  path={MENU_NAMES.PRODUCTS.key}
                  element={<ProductShell />}
                >
                  <Route
                    path={`${POWER_QUALITY_MENU.POWER_QUALITY.key}/${POWER_QUALITY_MENU.HARMONIC_REDUCTION_SOLUTION.key}`}
                    element={<HarmonicReductionCategory />}
                  />
                  <Route
                    path={`${POWER_QUALITY_MENU.POWER_QUALITY.key}/${POWER_QUALITY_MENU.HARMONIC_REDUCTION_SOLUTION.key}/${POWER_QUALITY_MENU.ACTIVE_HARMONIC_FILTER.key}`}
                    element={<ActiveHarmonicFilter />}
                  />
                  <Route
                    path={`${POWER_QUALITY_MENU.POWER_QUALITY.key}/${POWER_QUALITY_MENU.HARMONIC_REDUCTION_SOLUTION.key}/${POWER_QUALITY_MENU.ZERO_SEQUENCE_HARMONIC_FILTER.key}`}
                    element={<ZeroSequenceHarmonicFilter />}
                  />
                  <Route
                    path={`${POWER_QUALITY_MENU.POWER_QUALITY.key}/${POWER_QUALITY_MENU.REACTIVE_POWER_IMPROVEMENT_SOLUTION.key}`}
                    element={<ReactivePowerImprovementCategory />}
                  />
                  <Route
                    path={`${POWER_QUALITY_MENU.POWER_QUALITY.key}/${POWER_QUALITY_MENU.REACTIVE_POWER_IMPROVEMENT_SOLUTION.key}/${POWER_QUALITY_MENU.STATIC_VAR_GENERATOR.key}/${POWER_QUALITY_MENU.PSTA_SERIES.key}`}
                    element={<PSTASeries />}
                  />
                  <Route
                    path={`${POWER_QUALITY_MENU.POWER_QUALITY.key}/${POWER_QUALITY_MENU.REACTIVE_POWER_IMPROVEMENT_SOLUTION.key}/${POWER_QUALITY_MENU.STATIC_VAR_GENERATOR.key}/${POWER_QUALITY_MENU.PSVG_SERIES.key}`}
                    element={<PSVGSeries />}
                  />
                  <Route
                    path={`${POWER_QUALITY_MENU.POWER_QUALITY.key}/${POWER_QUALITY_MENU.REACTIVE_POWER_IMPROVEMENT_SOLUTION.key}/${POWER_QUALITY_MENU.PSTA_SERIES.key}`}
                    element={<NotFound />}
                  />
                  <Route
                    path={`${POWER_QUALITY_MENU.POWER_QUALITY.key}/${POWER_QUALITY_MENU.REACTIVE_POWER_IMPROVEMENT_SOLUTION.key}/${POWER_QUALITY_MENU.STATIC_VAR_COMPENSATOR.key}`}
                    element={<PSVCSeries />}
                  />
                  <Route
                    path={`${POWER_QUALITY_MENU.POWER_QUALITY.key}/${POWER_QUALITY_MENU.NOISE_REDUCTION_SOLUTION.key}`}
                    element={<NotFound />}
                  />
                  <Route
                    path={`${POWER_QUALITY_MENU.POWER_QUALITY.key}/${POWER_QUALITY_MENU.NOISE_REDUCTION_SOLUTION.key}/${POWER_QUALITY_MENU.NOISE_FILTER.key}`}
                    element={<NoiseFilter />}
                  />
                  <Route
                    path={`${POWER_QUALITY_MENU.POWER_QUALITY.key}/${POWER_QUALITY_MENU.NOISE_REDUCTION_SOLUTION.key}/${POWER_QUALITY_MENU.NOISE_CUT_TRANSFORMER.key}`}
                    element={<NoiseCutTransformer />}
                  />
                  <Route path={SPD_MENU.SPD.key} element={<SpdCategory />} />
                  {/* 「서지의 개요」는 서지 보호 페이지의 탭이지만 고유 주소를 갖는다. */}
                  <Route
                    path={`${SPD_MENU.SPD.key}/${SOLUTION_MENU.SURGE_INFO.key}`}
                    element={<SpdCategory />}
                  />
                  <Route
                    path={`${SPD_MENU.SPD.key}/${SPD_MENU.SPD_EXTERNAL_DISCONNECTOR.key}`}
                    element={<SpdExternalDisconnector />}
                  />
                  <Route
                    path={`${SPD_MENU.SPD.key}/${SPD_MENU.SPD_FOR_COMMUNICATION.key}`}
                    element={<SpdForCommunication />}
                  />
                  <Route
                    path={`${SPD_MENU.SPD.key}/${SPD_MENU.SPD_EXTRA.key}`}
                    element={<SPDExtra />}
                  />
                  <Route
                    path={`${SPD_MENU.SPD.key}/${SPD_MENU.SPD_FOR_POWER.key}`}
                    element={<NotFound />}
                  />
                  <Route
                    path={`${SPD_MENU.SPD.key}/${SPD_MENU.SPD_FOR_POWER.key}/${SPD_MENU.PS_SERIES.key}`}
                    element={<PsSeries />}
                  />
                  <Route
                    path={`${SPD_MENU.SPD.key}/${SPD_MENU.SPD_FOR_POWER.key}/${SPD_MENU.PS_DC_SERIES.key}`}
                    element={<PSDCSeries />}
                  />
                  <Route
                    path={`${SPD_MENU.SPD.key}/${SPD_MENU.SPD_FOR_POWER.key}/${SPD_MENU.R_SERIES.key}`}
                    element={<RSeries />}
                  />
                  <Route
                    path={`${SPD_MENU.SPD.key}/${SPD_MENU.SPD_FOR_POWER.key}/${SPD_MENU.SB_SERIES.key}`}
                    element={<SbSeries />}
                  />
                  <Route
                    path={`${SPD_MENU.SPD.key}/${SPD_MENU.SPD_FOR_POWER.key}/${SPD_MENU.SG_SERIES.key}`}
                    element={<SgSeries />}
                  />
                </Route>

                {/* Solutions */}
                <Route path={MENU_NAMES.SOLUTIONS.key} element={<Solutions />}>
                  <Route
                    path={SOLUTION_MENU.SPD_COORDINATION.key}
                    element={<SPDCoordination />}
                  />
                  <Route
                    path={SOLUTION_MENU.RENEWABLE_ENERGY_PROTECTION.key}
                    element={<NotFound />}
                  />
                  <Route
                    path={SOLUTION_MENU.SURGE_INFO.key}
                    element={<Surge />}
                  />
                  <Route
                    path={SOLUTION_MENU.HARMONIC_SOLUTION.key}
                    element={<HarmonicSolution />}
                  />
                  <Route
                    path={SOLUTION_MENU.REACTIVE_POWER_SOLUTION.key}
                    element={<ReactivePowerSolution />}
                  />
                  <Route
                    path={SOLUTION_MENU.SLPMS.key}
                    element={<NotFound />}
                  />
                </Route>

                <Route path={MENU_NAMES.CUSTOMER_SUPPORT.key}>
                  <Route
                    path={CUSTOMER_SUPPORT_MENU.INQUIRY.key}
                    element={<InquiryForm />}
                  />
                  <Route
                    path={CUSTOMER_SUPPORT_MENU.QUOTE.key}
                    element={<QuoteForm />}
                  />
                </Route>

                <Route path={MENU_NAMES.CONTACT.key}>
                  <Route
                    path={CONTACT_MENU.ABOUT_COMPANY.key}
                    element={<Company />}
                  />
                  <Route path={CONTACT_MENU.VIDEOS.key} element={<Videos />} />
                  <Route
                    path={CONTACT_MENU.CEO_GREETING.key}
                    element={<CEOGreeting />}
                  />
                  <Route
                    path={CONTACT_MENU.HISTORY.key}
                    element={<History />}
                  />
                  <Route path={CONTACT_MENU.NEWS.key} element={<News />} />
                  <Route
                    path={`${CONTACT_MENU.NEWS.key}/${CONTACT_MENU.NEWS_DETAIL.key}`}
                    element={<NewsDetail />}
                  />
                  <Route
                    path={CONTACT_MENU.LOCATION.key}
                    element={<Location />}
                  />
                  <Route
                    path={CONTACT_MENU.CUSTOMERS.key}
                    element={<Customers />}
                  />
                  <Route
                    path={CONTACT_MENU.ORGANIZATION.key}
                    element={<Organization />}
                  />
                </Route>

                {/* Resources */}
                <Route path={MENU_NAMES.RESOURCES.key}>
                  <Route
                    path={RESOURCE_MENU.CERTIFICATE.key}
                    element={<Data />}
                  />
                  <Route
                    path={`${RESOURCE_MENU.CERTIFICATE.key}/${RESOURCE_MENU.CERTIFICATE_DETAIL.key}/:id`}
                    element={<DataDetail />}
                  />
                  <Route
                    path={`${RESOURCE_MENU.CREATE_DOC.key}`}
                    element={<CreateDoc />}
                  />
                  <Route
                    path={RESOURCE_MENU.CATALOGS.key}
                    element={<Catalog />}
                  />
                  <Route
                    path={RESOURCE_MENU.PRE_QUALIFICATIONS.key}
                    element={<NotFound />}
                  />
                  <Route
                    path={RESOURCE_MENU.PATENT.key}
                    element={<NotFound />}
                  />
                </Route>

                <Route path="signin" element={<ProtectedSignIn />} />
                <Route path="verify-email" element={<VerifyEmailPage />} />
                <Route path="reset-password" element={<ResetPassword />} />
                <Route path="profile" element={<Profile />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </PageTransition>
          </Suspense>
        </Layout>
      </DataProvider>
    </AuthProvider>
  </Router>
);

export default AppRouter;
